import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import { ContentCopy, Refresh } from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import type { FieldPath, RegisterOptions } from 'react-hook-form';
import type { AccountFormValues } from '../../types/account';
import { generateApiKey } from '../../constants';

interface ApiKeyFieldProps extends Omit<TextFieldProps, 'name'> {
  name: FieldPath<AccountFormValues>;
  regeneratable?: boolean;
  copyNotifyKey?: string;
  rules?: Omit<
    RegisterOptions<AccountFormValues, FieldPath<AccountFormValues>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
}

export const ApiKeyField: React.FC<ApiKeyFieldProps> = ({
  name,
  regeneratable = false,
  copyNotifyKey = 'resources.accounts.notifications.apiKeyCopied',
  rules,
  ...rest
}) => {
  const { control, setValue, getValues } = useFormContext<AccountFormValues>();
  const notify = useNotify();
  const translate = useTranslate();

  const copy = async () => {
    const value = getValues(name);
    if (typeof value !== 'string' || !value) return;
    try {
      await navigator.clipboard.writeText(value);
      notify(copyNotifyKey, { type: 'success' });
    } catch {
    }
  };

  const regenerate = () => {
    setValue(name, generateApiKey() as never, { shouldDirty: true });
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <TextField
          {...rest}
          {...field}
          value={typeof field.value === 'string' ? field.value : ''}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? rest.helperText}
          InputLabelProps={{ shrink: true, ...(rest.InputLabelProps ?? {}) }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {regeneratable && (
                  <Tooltip
                    title={translate('resources.accounts.actions.regenerateApiKey')}
                  >
                    <IconButton size="small" onClick={regenerate} edge="end">
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={translate('resources.accounts.actions.copyApiKey')}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={copy}
                      edge="end"
                      disabled={
                        !(typeof field.value === 'string' && field.value)
                      }
                    >
                      <ContentCopy />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export default ApiKeyField;
